import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FileRequest } from '@uturn/api/v1';
import {
	ALLOWED_FILE_EXTENSIONS,
	DROPZONE_ACCEPT_MAP,
	FileDropzone,
	MAX_DESCRIPTION_CHARACTERS,
	MAX_UPLOAD_FILE_SIZE_BYTES,
	sanitizeFileName,
	validateDocumentFileName,
} from '@uturn/portal/components/file-dropzone';
import { MetaDataContext } from '@uturn/portal/context';
import { getFileTypeOptions } from '@uturn/portal/modules/shipments/utils';
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	SheetClose,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from '@uturn/ui-kit';
import type { ReactNode } from 'react';
import { useContext } from 'react';
import Dropzone, { FileRejection } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CharacterHint } from '../../character-hint';
import {
	type AddDocumentFormValues,
	addDocumentSchema,
} from './add-document.schema';

export type AddDocumentProps = {
	saveDocument: (file: File, fileRequest: Partial<FileRequest>) => void;
};

// TODO: BE shipmentMetaData.fileTypes could provide filterable attributes
//       in order to dynamically derive allowedFileTypes.
// TODO: BE shipmentMetaData.fileTypes could also provide sorting attribute to
//       control the order of the options. Currentlly, allowedFileTypes dictates
//       the sorting.

export function AddDocument({ saveDocument }: AddDocumentProps): ReactNode {
	const { t } = useTranslation();
	const { shipmentMetaData } = useContext(MetaDataContext);

	const fileTypeOptions = shipmentMetaData?.fileTypes
		? getFileTypeOptions(shipmentMetaData.fileTypes, t)
		: [];

	const form = useForm<AddDocumentFormValues>({
		resolver: zodResolver(addDocumentSchema),
		mode: 'onChange',
		defaultValues: {
			description: '',
			fileName: '',
		},
	});

	const fileUpload = form.watch('file');
	const description = form.watch('description');

	const onSubmit = (data: AddDocumentFormValues) => {
		saveDocument(data.file, {
			fileName: data.fileName,
			fileType: data.fileType,
			description: data.description,
		});
	};

	const handleOnDropRejected = (rejections: FileRejection[]) => {
		const first = rejections[0];
		const firstError = first?.errors?.[0];

		let message: string;
		switch (firstError?.code) {
			case 'file-too-large':
				message = t(
					'pages.shipment-detail.modal.add-document-too-large-error',
					'File must be 5 MB or smaller.',
				);
				break;

			case 'file-invalid-type':
				message = t(
					'pages.shipment-detail.modal.add-document-invalid-type-error',
					'Invalid file extension. Allowed: {{allowed}}.',
					{ allowed: ALLOWED_FILE_EXTENSIONS.join(', ') },
				);
				break;

			default:
				message = t(
					'pages.shipment-detail.modal.add-document-default-error',
					'File rejected.',
				);
				break;
		}

		form.resetField('file');
		form.setValue('fileName', '');
		form.setError('file', { type: 'custom', message });
	};

	const handleOnDrop = (files: File[]) => {
		const selectedFile = files?.[0];
		if (!selectedFile) {
			return;
		}

		const validationMessage = validateDocumentFileName(selectedFile.name);
		if (validationMessage) {
			form.resetField('file');
			form.setValue('fileName', '');
			form.setError('file', {
				type: 'custom',
				message: t(
					validationMessage.key,
					validationMessage.defaultMessage,
					validationMessage.params,
				),
			});
			return;
		}

		const normalizedFileName = sanitizeFileName(selectedFile.name);

		// Create a new File instance with the normalized name
		const fileWithNormalizedName = new File(
			[selectedFile],
			normalizedFileName,
			{
				type: selectedFile.type,
				lastModified: selectedFile.lastModified,
			},
		);

		form.clearErrors('file');
		form.setValue('fileName', normalizedFileName, { shouldValidate: true });
		form.setValue('file', fileWithNormalizedName, { shouldValidate: true });
	};

	const handleOnDelete = () => {
		form.resetField('file');
		form.setValue('fileName', '');
		form.clearErrors('file');
	};

	return (
		<Form {...form}>
			<>
				<SheetHeader>
					<SheetTitle>
						{t(
							'pages.shipment-detail.modal.add-document.heading',
							'Add document',
						)}
					</SheetTitle>
				</SheetHeader>

				<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:max-w-xl sm:grid-cols-6">
					<div className="col-span-full">
						<FormField
							control={form.control}
							name="fileType"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="required-asterix">
										{t(
											'pages.shipment-detail.modal.add-document.form.type.label',
											'Type',
										)}
									</FormLabel>
									<Select value={field.value} onValueChange={field.onChange}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue
													placeholder={
														<span>
															{t(
																'pages.shipment-detail.modal.add-document.form.type.placeholder',
																'Select a file type',
															)}
														</span>
													}
												/>
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{fileTypeOptions.map((option) => (
												<SelectItem key={option.value} value={option.value}>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className="col-span-full">
						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t(
											'pages.shipment-detail.modal.add-document.form.description',
											'Description',
										)}
									</FormLabel>
									<FormControl>
										<Input maxLength={MAX_DESCRIPTION_CHARACTERS} {...field} />
									</FormControl>
									<CharacterHint
										currentCount={description ? description.length : 0}
										maxCharacters={MAX_DESCRIPTION_CHARACTERS}
										translationKey="shipment-document.description.hint"
										defaultMessage="You can type up to {{count}} characters."
									/>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className="col-span-full">
						<FormField
							control={form.control}
							name="file"
							render={() => (
								<FormItem>
									{fileUpload ? (
										<section className="max-w-xl">
											<div className="bg-secondary-50/20 border-secondary-300 flex h-40 w-full appearance-none items-center justify-center rounded-md border-2 border-dashed p-4 transition focus:outline-none">
												<div className="flex items-center">
													<span className="text-sm">{fileUpload.name}</span>
													<Button
														type="button"
														variant="ghost"
														onClick={handleOnDelete}
													>
														<Icon
															icon={faTrashCan}
															className="-ml-1 size-4 pt-0.5"
														/>
													</Button>
												</div>
											</div>
										</section>
									) : (
										<Dropzone
											onDrop={handleOnDrop}
											onDropRejected={handleOnDropRejected}
											maxSize={MAX_UPLOAD_FILE_SIZE_BYTES}
											accept={DROPZONE_ACCEPT_MAP}
										>
											{({ getRootProps, getInputProps }) => (
												<FileDropzone
													getInputProps={getInputProps}
													getRootProps={getRootProps}
												/>
											)}
										</Dropzone>
									)}
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className="col-span-full">
						<SheetFooter>
							<SheetClose asChild>
								<Button
									type="button"
									size="sm"
									onClick={() => form.handleSubmit(onSubmit)()}
									disabled={!form.formState.isValid}
								>
									{t(
										'pages.shipment-detail.modal.add-document.form.submit',
										'Save',
									)}
								</Button>
							</SheetClose>
						</SheetFooter>
					</div>
				</div>
			</>
		</Form>
	);
}
